import { ORDER_TRACKING_BACKFILL_DAYS } from "@redotech/order-tracking-common/constants";
import { OTV2_DEFAULT_MAX_ORDER_AGE_DAYS } from "@redotech/order-tracking-common/order-tracking-types";
import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { Card } from "@redotech/redo-web/card";
import { Divider } from "@redotech/redo-web/divider";
import { Flex } from "@redotech/redo-web/flex";
import * as gridCss from "@redotech/redo-web/grid.module.css";
import { Text } from "@redotech/redo-web/text";
import { InputSchema, groupInput, input } from "@redotech/ui/form";
import { numberValidator } from "@redotech/ui/validators";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { memo, useContext } from "react";
import { RedoAdminRpcClientContext } from "../../../app/redo-admin-rpc-client-provider";
import { useHasMarketingPermission } from "../../../marketing/use-marketing-permissions";
import { TeamContext } from "../../team";
import { IntegrationEventTestCard } from "./integration-event-test-card";
import { KlaviyoMetricsHealthCheckCard } from "./klaviyo-metrics-health-check";
import { MalomoMigrationCard } from "./malomo-migration-card";
import { ScanKlaviyoTemplatesCard } from "./scan-klaviyo-templates-for-malomo-links";

const POLL_FREQUENCY_MS = 3000;

export const orderTrackingV2Form = groupInput({
  setTrackingUrl: input<boolean>(),
  setTrackingUrlOnSource: input<boolean>(),
  customTrackingPage: input<boolean>(),
  defaultTrackingPageUrl: input<string>({}),
  backfillDays: input<string>({
    validator: numberValidator({
      min: 1,
      max: OTV2_DEFAULT_MAX_ORDER_AGE_DAYS,
    }),
  }),
});

export type OrderTrackingV2Form = InputSchema.Input<typeof orderTrackingV2Form>;

export type OrderTrackingV2Value = InputSchema.Value<
  typeof orderTrackingV2Form
>;

export const orderTrackingV2Default: OrderTrackingV2Value = {
  setTrackingUrl: false,
  setTrackingUrlOnSource: false,
  customTrackingPage: false,
  defaultTrackingPageUrl: "",
  backfillDays: String(ORDER_TRACKING_BACKFILL_DAYS),
};

export const OrderTrackingSettingsCards = memo(
  function OrderTrackingSettingsCards({
    input,
  }: {
    input: OrderTrackingV2Form;
  }) {
    const isAuthorized = useHasMarketingPermission("marketing_admin");
    const team = useContext(TeamContext);
    const rpcClient = useContext(RedoAdminRpcClientContext);
    const queryClient = useQueryClient();

    const { data: progressData } = useQuery({
      queryKey: ["orderTrackingBackfillProgress", team?._id],
      queryFn: () => {
        if (!team || !rpcClient) {
          throw new Error("Team or RPC client not available");
        }
        return rpcClient.getOrderTrackingBackfillProgress({ teamId: team._id });
      },
      enabled: !!team && !!rpcClient,
      refetchInterval: (query) => {
        const data = query.state.data;
        return data?.found && data?.running ? POLL_FREQUENCY_MS : false;
      },
      refetchOnWindowFocus: false,
    });

    const isRunning = progressData?.found && progressData?.running;
    const isCompleted =
      progressData?.found && !progressData?.running && progressData?.progress;
    const progress = isRunning ? progressData.progress : null;
    const finalStats = isCompleted ? progressData.progress : null;

    const triggerBackfillMutation = useMutation({
      mutationFn: async () => {
        if (!team || !rpcClient) {
          throw new Error("Team or RPC client not available");
        }
        const days =
          parseInt(input.inputs.backfillDays.value) ||
          ORDER_TRACKING_BACKFILL_DAYS;
        return rpcClient.triggerOrderTrackingBackfill({
          teamId: team._id,
          days,
        });
      },
      onSuccess: (response) => {
        if (response.success) {
          toast(
            `Backfill started successfully. Workflow ID: ${response.workflowId}`,
            { variant: "success" },
          );
          void queryClient.invalidateQueries({
            queryKey: ["orderTrackingBackfillProgress", team?._id],
          });
        } else {
          toast(response.error || "Unknown error occurred", {
            variant: "error",
          });
        }
      },
      onError: (error) => {
        const errorMessage =
          error instanceof Error ? error.message : "Unknown error";
        toast(`Failed to start backfill: ${errorMessage}`, {
          variant: "error",
        });
      },
    });

    return (
      <Flex dir="column" gap="lg">
        <Card title="Tracking page">
          <section className={gridCss.grid}>
            <div className={gridCss.span6L}>
              <RedoToggle
                description="Store the tracking page URL internally in Redo for each order and update the URL in shipping confirmation/update emails"
                disabled={!isAuthorized}
                label="Set Tracking URL"
                setValue={input.inputs.setTrackingUrl.setValue}
                size="sm"
                value={input.inputs.setTrackingUrl.value}
              />
            </div>
            {/* design-allow: grid span matching existing pattern in this file */}
            <div className={gridCss.span6L}>
              <RedoToggle
                description="Merchant uses a custom tracking page integration. Health checks will verify HTTP 200 only (no Shopify block check)."
                disabled={!isAuthorized}
                label="Custom Tracking Page"
                setValue={input.inputs.customTrackingPage.setValue}
                size="sm"
                value={input.inputs.customTrackingPage.value}
              />
            </div>
            {/* design-allow: grid span matching existing pattern in this file */}
            <div className={gridCss.span6L}>
              <RedoToggle
                description='Update the tracking page URL on Shopify fulfillments. Requires "Set Tracking URL" to be enabled. Only enable this if the merchant sends tracking emails through Shopify.'
                disabled={!isAuthorized || !input.inputs.setTrackingUrl.value}
                label="Set Tracking URL on Source"
                setValue={input.inputs.setTrackingUrlOnSource.setValue}
                size="sm"
                value={input.inputs.setTrackingUrlOnSource.value}
              />
            </div>
            <div className={gridCss.span12}>
              <Flex dir="column" gap="sm">
                <RedoTextInput
                  inputTypeHint="url"
                  label="Default Tracking Page URL"
                  placeholder="https://example.com/tracking"
                  setValue={input.inputs.defaultTrackingPageUrl.setValue}
                  state={!isAuthorized ? "disabled" : "default"}
                  value={input.inputs.defaultTrackingPageUrl.value}
                />
                <Text fontSize="xs">URL used for embedded tracking pages</Text>
              </Flex>
            </div>
          </section>
        </Card>
        <Card title="Order Tracking tools">
          <Flex dir="column" gap="lg">
            <Flex dir="column" gap="sm">
              <Text fontSize="md" fontWeight="semibold">
                Order Tracking Backfill
              </Text>
              <Flex align="flex-end" gap="md">
                <RedoTextInput
                  errorText={input.inputs.backfillDays.errors[0]}
                  inputTypeHint="number"
                  label={`Days to backfill (max ${OTV2_DEFAULT_MAX_ORDER_AGE_DAYS})`}
                  max={OTV2_DEFAULT_MAX_ORDER_AGE_DAYS}
                  min={1}
                  placeholder={String(ORDER_TRACKING_BACKFILL_DAYS)}
                  setValue={input.inputs.backfillDays.setValue}
                  state={
                    !isAuthorized
                      ? "disabled"
                      : input.inputs.backfillDays.errors.length
                        ? "error"
                        : "default"
                  }
                  value={input.inputs.backfillDays.value}
                />
                <RedoButton
                  disabled={
                    !isAuthorized ||
                    input.inputs.backfillDays.errors.length > 0 ||
                    isRunning ||
                    triggerBackfillMutation.isPending ||
                    !team ||
                    !rpcClient
                  }
                  hierarchy="primary"
                  onClick={() => triggerBackfillMutation.mutate()}
                  size="sm"
                  text={
                    isRunning
                      ? "Running..."
                      : triggerBackfillMutation.isPending
                        ? "Starting..."
                        : "Start backfill"
                  }
                />
              </Flex>
              <Text fontSize="xs">
                Backfill existing orders into Order Tracking workflows. Each
                backfilled order has a 2-hour notification quiet period,
                including live updates during that window. New orders outside
                the backfill are unaffected.
              </Text>
              {isRunning && progress && (
                <Flex dir="column" gap="xs">
                  <Text fontSize="sm" fontWeight="medium">
                    Backfill in progress: {progress.progressPercent}
                  </Text>
                  <Text fontSize="sm">
                    {progress.totalProcessed} / {progress.totalCount} processed
                    ({progress.workflowsStarted} started,{" "}
                    {progress.skippedNoShopify} skipped)
                  </Text>
                  {progress.errors > 0 && (
                    <Text fontSize="sm" textColor="error">
                      Errors: {progress.errors}
                    </Text>
                  )}
                </Flex>
              )}
              {isCompleted && finalStats && (
                <Flex dir="column" gap="xs">
                  <Text fontSize="sm" fontWeight="medium" textColor="success">
                    Backfill completed
                  </Text>
                  <Text fontSize="sm">
                    {finalStats.totalProcessed} / {finalStats.totalCount}{" "}
                    processed ({finalStats.workflowsStarted} started,{" "}
                    {finalStats.skippedNoShopify} skipped)
                  </Text>
                  {finalStats.errors > 0 && (
                    <Text fontSize="sm" textColor="error">
                      Errors: {finalStats.errors}
                    </Text>
                  )}
                </Flex>
              )}
            </Flex>
            <Divider />
            <IntegrationEventTestCard />
            <MalomoMigrationCard />
            <ScanKlaviyoTemplatesCard />
            <KlaviyoMetricsHealthCheckCard />
          </Flex>
        </Card>
      </Flex>
    );
  },
);
